import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Alert,
  Divider,
} from "@mui/material";
import { green } from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";

const headerTitleStyle = {
  fontWeight: "bold",
  color: "white",
  flex: 1,
  textAlign: "center",
};
const closeBtnStyle = {
  color: "yellow",
  cursor: "pointer",
  "&:hover": { color: "white" },
};
const sectionBoxStyle = {
  p: 1.5,
  bgcolor: green[50],
  borderRadius: 1,
  border: `1px solid ${green[200]}`,
};
const tableHeaderStyle = {
  fontWeight: "bold",
  color: "white",
  backgroundColor: green[700],
  padding: "10px 12px",
};

const EXTRACTION_GROUPS = [
  {
    id: "chat",
    label: "대화방",
    items: [
      {
        id: "chat_mobilization",
        label: "동원대화방",
        desc: "공지·동원자원 채팅",
      },
      { id: "chat_support", label: "지원인력대화방", desc: "지원인력 채팅" },
    ],
  },
  {
    id: "history",
    label: "지령상태이력",
    items: [
      { id: "order", label: "지령", desc: "" },
      { id: "memo", label: "메모", desc: "" },
      { id: "status", label: "상태", desc: "" },
      { id: "support", label: "지원", desc: "" },
    ],
  },
];

export default function ActivityTypesConfigModal({ open, onClose, apiAddr }) {
  const [extractionTargets, setExtractionTargets] = useState({
    chat_mobilization: true,
    chat_support: true,
    order: true,
    memo: true,
    status: false,
    support: false,
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingTargets, setSavingTargets] = useState(false);
  const [savingTypes, setSavingTypes] = useState(false);
  const [message, setMessage] = useState({ type: null, text: "" });

  const load = useCallback(async () => {
    if (!apiAddr || !open) return;
    setLoading(true);
    setMessage({ type: null, text: "" });
    try {
      const extUrl = `${apiAddr}/get/extractionTargetsConfig?t=${Date.now()}`;
      console.log("[추출대상] 조회 요청:", extUrl);
      const [actRes, extRes] = await Promise.all([
        fetch(`${apiAddr}/get/activityTypesConfig?t=${Date.now()}`, {
          cache: "no-store",
        }),
        fetch(extUrl, { cache: "no-store" }),
      ]);
      const actData = await actRes.json().catch(() => []);
      const extData = await extRes.json().catch(() => ({}));
      console.log("[추출대상] 조회 응답:", {
        status: extRes.status,
        ok: extRes.ok,
        raw: extData,
      });
      const list = Array.isArray(actData)
        ? actData
        : actData?.items
          ? actData.items
          : [];
      if (extData && typeof extData === "object") {
        const merged = {
          chat_mobilization: true,
          chat_support: true,
          order: true,
          memo: true,
          status: false,
          support: false,
        };
        if (
          extData.chat !== undefined &&
          extData.chat_mobilization === undefined
        ) {
          merged.chat_mobilization = !!extData.chat;
          merged.chat_support = !!extData.chat;
        }
        [
          "chat_mobilization",
          "chat_support",
          "order",
          "memo",
          "status",
          "support",
        ].forEach((k) => {
          if (extData[k] !== undefined) merged[k] = !!extData[k];
        });
        console.log("[추출대상] 적용된 설정:", merged);
        setExtractionTargets(merged);
      }
      setItems(
        list.length > 0
          ? list.map((r) => ({
              code: r.code ?? "",
              label_kr: r.label_kr ?? "",
              keywords: Array.isArray(r.keywords) ? r.keywords.join(", ") : "",
              quantity_units:
                Array.isArray(r.quantity_units) && r.quantity_units.length > 0
                  ? r.quantity_units
                      .map((qu) => `${qu.unit}(${qu.label_kr})`)
                      .join(", ")
                  : "",
            }))
          : [{ code: "", label_kr: "", keywords: "", quantity_units: "" }],
      );
    } catch (e) {
      console.error("[추출대상] 조회 예외:", e);
      setMessage({ type: "error", text: "설정을 불러오지 못했습니다." });
      setItems([{ code: "", label_kr: "", keywords: "", quantity_units: "" }]);
    } finally {
      setLoading(false);
    }
  }, [apiAddr, open]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleChange = (index, field, value) => {
    setItems((prev) => {
      const next = prev.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      );
      return next;
    });
  };

  const handleAdd = () => {
    setItems((prev) => [
      ...prev,
      { code: "", label_kr: "", keywords: "", quantity_units: "" },
    ]);
  };

  const handleRemove = (index) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTargetChange = (id, checked) => {
    setExtractionTargets((prev) => {
      const next = { ...prev, [id]: checked };
      console.log("[추출대상] 토글 변경:", id, checked, "→", next);
      return next;
    });
  };

  const handleSaveTargets = async () => {
    const activeCount = Object.values(extractionTargets).filter(Boolean).length;
    if (activeCount === 0) {
      console.log("[추출대상] 저장 스킵: 선택 항목 없음");
      setMessage({
        type: "warning",
        text: "최소 1개 이상의 추출대상을 선택하세요.",
      });
      return;
    }
    const saveUrl = `${apiAddr}/post/extractionTargetsConfig`;
    console.log(
      "[추출대상] 저장 요청:",
      saveUrl,
      "payload:",
      extractionTargets,
    );
    setSavingTargets(true);
    setMessage({ type: null, text: "" });
    try {
      const res = await fetch(saveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(extractionTargets),
      });
      const json = await res.json().catch(() => ({}));
      console.log("[추출대상] 저장 응답:", {
        status: res.status,
        ok: res.ok,
        body: json,
      });
      if (res.ok && (json.result === "success" || !json.result)) {
        setMessage({
          type: "success",
          text: json.message || "추출대상 설정이 저장되었습니다.",
        });
        console.log("[추출대상] 저장 성공, 설정 재조회");
        load();
      } else {
        console.warn("[추출대상] 저장 실패:", json.message || res.status);
        setMessage({
          type: "error",
          text: json.message || `저장 실패 (${res.status})`,
        });
      }
    } catch (e) {
      console.error("[추출대상] 저장 요청 예외:", e);
      setMessage({ type: "error", text: "저장 요청에 실패했습니다." });
    } finally {
      setSavingTargets(false);
    }
  };

  const parseQuantityUnits = (s) => {
    if (!s || typeof s !== "string") return [];
    return (s || "")
      .split(/[,，]/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => {
        // 형식1: 단위(한글라벨) — 반각·전각 괄호 모두 지원
        let m = p.match(/^(.+?)[(（](.+)[)）]$/);
        if (m) return { unit: m[1].trim(), label_kr: m[2].trim() };
        // 형식2: 단위:한글라벨
        m = p.match(/^(.+?):(.+)$/);
        if (m) return { unit: m[1].trim(), label_kr: m[2].trim() };
        return null;
      })
      .filter(Boolean);
  };

  const handleSave = async () => {
    const payload = items
      .map((r) => {
        const qus = parseQuantityUnits(r.quantity_units);
        return {
          code: (r.code || "").trim() || undefined,
          label_kr: (r.label_kr || "").trim() || undefined,
          keywords: (r.keywords || "")
            .split(/[,，]/)
            .map((k) => k.trim())
            .filter(Boolean),
          ...(qus.length > 0 ? { quantity_units: qus } : {}),
        };
      })
      .filter((r) => r.code || r.label_kr);
    if (payload.length === 0) {
      setMessage({
        type: "warning",
        text: "최소 1개 이상의 활동유형을 입력하세요.",
      });
      return;
    }
    setSavingTypes(true);
    setMessage({ type: null, text: "" });
    try {
      const res = await fetch(`${apiAddr}/post/activityTypesConfig`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: payload }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && (json.result === "success" || !json.result)) {
        setMessage({
          type: "success",
          text: json.message || "저장되었습니다.",
        });
        load();
      } else {
        setMessage({
          type: "error",
          text: json.message || `저장 실패 (${res.status})`,
        });
      }
    } catch (e) {
      setMessage({ type: "error", text: "저장 요청에 실패했습니다." });
    } finally {
      setSavingTypes(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          maxWidth: 1100,
          position: "fixed",
          top: "10vh",
          left: "50%",
          transform: "translateX(-50%)",
          margin: 0,
          maxHeight: "80vh",
        },
      }}
    >
      {/* 헤더: QR코드관리·자원집결지관리와 동일한 진한 녹색 바 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 10px",
          backgroundColor: green[900],
          color: "white",
          minHeight: 52,
        }}
      >
        <CloseIcon sx={closeBtnStyle} onClick={onClose} aria-label="닫기" />
        <Typography variant="h6" sx={headerTitleStyle}>
          실적관리 설정 — 활동유형·키워드 설정
        </Typography>
        <Box sx={{ width: 40 }} />
      </Box>
      <DialogContent sx={{ backgroundColor: "#fff", padding: 2 }}>
        {/* 실적추출대상 */}
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{ mb: 1, color: "text.primary" }}
        >
          실적추출대상
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          소방실적 집계에 포함할 데이터 출처를 선택하세요.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
          {EXTRACTION_GROUPS.map((grp) => (
            <Box key={grp.id} sx={sectionBoxStyle}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                {grp.label}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, pl: 1 }}>
                {grp.items.map((t) => (
                  <FormControlLabel
                    key={t.id}
                    control={
                      <Switch
                        size="small"
                        checked={!!extractionTargets[t.id]}
                        onChange={(e) =>
                          handleTargetChange(t.id, e.target.checked)
                        }
                        disabled={loading}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t.label}
                        {t.desc ? (
                          <span
                            style={{
                              color: "var(--mui-palette-text-secondary)",
                              fontSize: "0.75rem",
                            }}
                          >
                            {" "}
                            ({t.desc})
                          </span>
                        ) : null}
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSaveTargets}
          disabled={savingTargets}
          sx={{ mb: 2, borderColor: green[600], color: green[700] }}
        >
          {savingTargets ? "저장 중…" : "추출대상 저장"}
        </Button>
        <Divider sx={{ mb: 2 }} />
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{ mb: 1, color: "text.primary" }}
        >
          활동유형·키워드
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          대시보드 소방실적에서 사용하는 활동 유형과 매칭 키워드를 관리합니다.
          키워드는 쉼표로 구분하여 입력하세요.
        </Typography>
        {message.text && (
          <Alert
            severity={message.type || "info"}
            sx={{ mb: 2 }}
            onClose={() => setMessage({ type: null, text: "" })}
          >
            {message.text}
          </Alert>
        )}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ mb: 2, borderRadius: 1, overflow: "hidden" }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeaderStyle, width: 120 }}>
                  코드
                </TableCell>
                <TableCell sx={{ ...tableHeaderStyle, width: 100 }}>
                  활동 유형(한글)
                </TableCell>
                <TableCell sx={{ ...tableHeaderStyle, width: 240 }}>
                  키워드 (쉼표 구분)
                </TableCell>
                <TableCell sx={{ ...tableHeaderStyle, width: 200 }}>
                  계량 단위 (예: 톤(급수톤수), 채(민가))
                </TableCell>
                <TableCell sx={{ ...tableHeaderStyle, width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    불러오는 중…
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="예: fire_suppress"
                        value={row.code}
                        onChange={(e) =>
                          handleChange(index, "code", e.target.value)
                        }
                        inputProps={{ style: { fontSize: "0.875rem" } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="예: 화재진압"
                        value={row.label_kr}
                        onChange={(e) =>
                          handleChange(index, "label_kr", e.target.value)
                        }
                        inputProps={{ style: { fontSize: "0.875rem" } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="예: 화재진압, 진화, 소화"
                        value={row.keywords}
                        onChange={(e) =>
                          handleChange(index, "keywords", e.target.value)
                        }
                        inputProps={{ style: { fontSize: "0.875rem" } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="톤(급수톤수), 채(민가)"
                        value={row.quantity_units ?? ""}
                        onChange={(e) =>
                          handleChange(index, "quantity_units", e.target.value)
                        }
                        inputProps={{ style: { fontSize: "0.875rem" } }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(index)}
                        disabled={items.length <= 1}
                        aria-label="삭제"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={loading}
            sx={{ borderColor: green[600], color: green[700] }}
          >
            행 추가
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading || savingTypes}
            sx={{
              fontWeight: "bold",
              backgroundColor: green[600],
              "&:hover": { backgroundColor: green[800] },
            }}
          >
            {savingTypes ? "저장 중…" : "저장"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
